import type { OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { Overlay } from '@angular/cdk/overlay';
import type { ComponentType } from '@angular/cdk/portal';
import { ComponentPortal } from '@angular/cdk/portal';
import type {
  ComponentRef,
  InjectionToken,
  ViewContainerRef,
} from '@angular/core';
import { Injector, inject, signal } from '@angular/core';
import { Subject } from 'rxjs';

export class OnyxBaseOverlayService {
  protected readonly overlay = inject(Overlay);

  private _overlayRef = signal<OverlayRef | null>(null);
  public get hasAttached() {
    return this._overlayRef()?.hasAttached();
  }
  public get overlayRef() {
    return this._overlayRef.asReadonly();
  }

  private _componentRef = signal<ComponentRef<unknown> | null>(null);
  public get componentRef() {
    return this._componentRef.asReadonly();
  }

  private _detach$ = new Subject<void>();
  public get detach$() {
    return this._detach$.asObservable();
  }

  public createComponentInjector<T>(
    token: InjectionToken<T>,
    useValue: Partial<T>,
  ): Injector {
    return Injector.create({
      providers: [
        {
          provide: token,
          useValue,
        },
      ],
    });
  }

  public createComponentPortal<T>(
    component: ComponentType<T>,
    injector?: Injector,
    viewContainerRef?: ViewContainerRef,
    projectableNodes?: Node[][],
  ): ComponentPortal<T> {
    return new ComponentPortal(
      component,
      viewContainerRef,
      injector,
      projectableNodes,
    );
  }

  public createOverlayRef(config?: OverlayConfig): OverlayRef {
    return this.overlay.create(config);
  }

  public setOverlayRef(overlayRef: OverlayRef): void {
    if (this._overlayRef()) this._overlayRef()?.dispose();
    this._overlayRef.set(overlayRef);
  }

  public attachToOverlay<T>(componentPortal: ComponentPortal<T>): void {
    const overlayRef = this.overlayRef();
    if (!overlayRef || overlayRef.hasAttached()) return;

    const componentRef = overlayRef.attach(componentPortal);
    this._componentRef.set(componentRef);
  }

  public detachFromOverlay(): void {
    this._overlayRef()?.detach();
    this._detach$.next();

    const componentRef = this.componentRef();
    if (!componentRef) return;

    componentRef.destroy();
    this._componentRef.set(null);
  }

  public addPanelClass(className: string): void {
    return this._overlayRef()?.addPanelClass(className);
  }

  public removePanelClass(className: string): void {
    return this._overlayRef()?.removePanelClass(className);
  }
}
